"use client";

import GovernedOfferPlacement from "@/components/GovernedOfferPlacement";
import { trackAgentRevenueOfferClicked } from "@/lib/analytics-agent-offers";
import { trackOfferCtaTapped } from "@/lib/analytics-offer-milestones";
import type { BlogCommerceOffer } from "@/lib/blog-posts";
import {
  SHOPIFY_COMMERCE_SYSTEM,
  SHOPIFY_PRODUCT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import { RUNTIME_OFFER_PLACEMENT_KARUNGALI_GUIDE_PRODUCT_OFFER } from "@/lib/offer-placement-runtime-keys.generated";

const SOURCE_PATH = "/blog/karungali-mala-guide";
const INTENT = "dosha_remedy" as const;
const ROUTE_REASON = "fixed_physical_pilot|karungali_buyer_guide";

function productTarget(href: string): string {
  const params = new URLSearchParams({
    src: RUNTIME_OFFER_PLACEMENT_KARUNGALI_GUIDE_PRODUCT_OFFER,
    source: SOURCE_PATH,
    intent: INTENT,
    customer_problem: "remedy",
  });
  return `${href}${href.includes("?") ? "&" : "?"}${params.toString()}`;
}

/** One governed, post-value commerce action for the fixed Karungali buyer-guide pilot. */
export default function BlogProductOffer({ offer }: { offer: BlogCommerceOffer }) {
  return (
    <GovernedOfferPlacement
      placement={RUNTIME_OFFER_PLACEMENT_KARUNGALI_GUIDE_PRODUCT_OFFER}
      planId={offer.offerId}
      actionMode="direct_checkout"
      sourcePath={SOURCE_PATH}
      intent={INTENT}
      offerType="product"
      productSku={offer.offerId}
      productName={offer.name}
      checkoutChannel={SHOPIFY_PRODUCT_CHECKOUT_CHANNEL}
      commerceSystem={SHOPIFY_COMMERCE_SYSTEM}
      customerProblem="remedy"
      routeRankReason={ROUTE_REASON}
      rankedOfferOrder={[offer.offerId]}
      ready
      className="mt-10 rounded-2xl border border-border-subtle bg-surface-brand-subtle p-5 sm:p-6"
    >
      {({ contract }) => (
        <section aria-labelledby="karungali-product-offer-heading">
          <h2
            id="karungali-product-offer-heading"
            className="font-serif text-title font-bold text-text-primary"
          >
            {offer.headline}
          </h2>
          <p className="mt-2 text-body leading-relaxed text-text-secondary">{offer.description}</p>
          <a
            href={productTarget(offer.href)}
            className="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl bg-surface-brand-strong px-5 py-2.5 text-body font-semibold text-text-on-brand"
            onClick={() => {
              const milestone = {
                plan_id: contract.planId,
                placement: contract.placement,
                source_path: contract.sourcePath,
                intent: contract.intent,
                presentation_id: contract.presentationId,
                offer_action_mode: "direct_checkout" as const,
                is_primary_cta: true,
              };
              trackAgentRevenueOfferClicked({
                offer_type: "product",
                plan_id: contract.planId,
                product_sku: offer.offerId,
                product_name: offer.name,
                source_path: contract.sourcePath,
                intent: contract.intent,
                placement: contract.placement,
                checkout_channel: SHOPIFY_PRODUCT_CHECKOUT_CHANNEL,
                commerce_system: SHOPIFY_COMMERCE_SYSTEM,
                customer_problem: "remedy",
                route_rank_reason: ROUTE_REASON,
                ranked_offer_order: [offer.offerId],
                is_primary_cta: true,
                presentation_id: contract.presentationId,
              });
              trackOfferCtaTapped(milestone);
            }}
          >
            {offer.cta}
            <span aria-hidden="true"> →</span>
          </a>
        </section>
      )}
    </GovernedOfferPlacement>
  );
}
